// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { xBaseTheme } from "@pacific/XDL/types";
import color from "@pacific/XDL/Utilities/color";
import { getSystemColor } from "@pacific/XDL/Utilities/color";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: 0;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

/**
 * Loading style: show 3 dots with animation
 * <Button>
 *   <StyledWrapper {...loadingButtonStyle} />
 * </Button>
 */
export const loadingButtonStyle = (loadingColor = "common.ui1") => {
    return {
        css: css`
            position: relative;
            left: -9999px;
            width: 3px;
            height: 3px;
            border-radius: 5px;
            background-color: ${color(loadingColor)};
            color: ${color(loadingColor)};
            box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
            animation: dotPulse 1.5s infinite linear;
            animation-delay: 0.25s;

            &::before,
            &::after {
                content: "";
                display: inline-block;
                position: absolute;
                top: 0;
                width: 3px;
                height: 3px;
                border-radius: 5px;
                background-color: ${color(loadingColor)};
                color: ${color(loadingColor)};
            }

            &::before {
                box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                animation: dotPulseBefore 1.5s infinite linear;
                animation-delay: 0s;
            }

            &::after {
                box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                animation: dotPulseAfter 1.5s infinite linear;
                animation-delay: 0.5s;
            }

            @keyframes dotPulseBefore {
                0% {
                    box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 9984px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 9984px 0 0 -5px ${color(loadingColor)};
                }
            }

            @keyframes dotPulse {
                0% {
                    box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 9999px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 9999px 0 0 -5px ${color(loadingColor)};
                }
            }

            @keyframes dotPulseAfter {
                0% {
                    box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                }
                30% {
                    box-shadow: 10014px 0 0 2px ${color(loadingColor)};
                }
                60%,
                100% {
                    box-shadow: 10014px 0 0 -5px ${color(loadingColor)};
                }
            }
        `,
    };
};

export const buttonColors: { [key: string]: { main: string; hover: string; text: string; focus: string } } = {
    primary: {
        main: "primary",
        hover: "common.ui8",
        text: "common.ui1",
        focus: "common.ui9",
    },
    tertiary: {
        main: "primary",
        hover: "common.ui8",
        text: "primary",
        focus: "common.ui9",
    },
};

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.primary.main,
        buttonType: "solid",
        mergeCss: true,
        hoverStyle: {
            background: `${getSystemColor(buttonColors.primary.hover)}`,
            borderColor: `${getSystemColor(buttonColors.primary.hover)}`,
            color: `${getSystemColor(buttonColors.primary.hover)}`,
        },
        css: css`
            &:focus {
                background: ${getSystemColor(buttonColors.primary.focus)};
                border-color: ${getSystemColor(buttonColors.primary.focus)};
            }
            &:disabled {
                background: ${getSystemColor(buttonColors.primary.hover, 0.33)};
                border-color: transparent;
            }
        `,
    },
    secondary: {
        mergeCss: true,
        ...ButtonPresentationPropsDefault,
    },
    tertiary: {
        ...ButtonPresentationPropsDefault,
        color: buttonColors.tertiary.main,
        buttonType: "outline",
        mergeCss: true,
        hoverStyle: {
            borderColor: `${getSystemColor(buttonColors.tertiary.hover)}`,
            color: `${getSystemColor(buttonColors.tertiary.hover)}`,
        },
        css: css`
            border: 1px solid;
            &:focus {
                border-color: ${getSystemColor(buttonColors.tertiary.focus)};
                color: ${getSystemColor(buttonColors.tertiary.focus)};
            }
            &:disabled {
                border-color: ${getSystemColor(buttonColors.tertiary.main, 0.33)};
                color: ${getSystemColor(buttonColors.tertiary.main, 0.33)};
            }
        `,
    },
} as xBaseTheme["button"];
